// @flow

import * as React from 'react';

type Props<T> = {|
  data: Array<T>,
  identifier: T => string,
  addToStart?: boolean,
  children: (Array<T>) => React.Node,
  onDataUpdate?: (Array<T>) => void,
  /** A trigger to reset the data state */
  reset?: boolean,
|};
type State<T> = {|
  accumulatedData: Array<T>,
  ids: Set<string>,
|};

/**
 * This Provider allows you to send data in chunks but pass all previous data through
 * to child consumers. Useful for progressive loading, where you download N items at a time, but want
 * to display all downloaded items.
 */
class AccumulatingProvider<T> extends React.Component<Props<T>, State<T>> {
  static getDerivedStateFromProps(props: Props<T>, state: State<T>): State<T> | null {
    if (props.reset) {
      return {
        accumulatedData: props.data,
        ids: new Set(props.data.map(props.identifier)),
      };
    }

    const newItems = props.data.filter(item => !state.ids.has(props.identifier(item)));
    if (newItems.length === 0) {
      return null;
    }

    return {
      accumulatedData: props.addToStart
        ? newItems.concat(state.accumulatedData)
        : state.accumulatedData.concat(newItems),
      ids: new Set([...state.ids, ...newItems.map(props.identifier)]),
    };
  }

  constructor(props: Props<T>) {
    super(props);
    this.state = {
      accumulatedData: this.props.data,
      ids: new Set(this.props.data.map(this.props.identifier)),
    };
    this.previousData = this.props.data;
  }

  componentDidUpdate() {
    if (this.props.onDataUpdate && this.state.accumulatedData.length > this.previousData.length) {
      this.previousData = this.state.accumulatedData;
      this.props.onDataUpdate(this.state.accumulatedData);
    }
  }

  previousData: Array<T>;

  render() {
    return this.props.children(this.state.accumulatedData);
  }
}

export default AccumulatingProvider;
